import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Helmet } from 'react-helmet-async';

import { THEMES, PLAY_MODE, PLAY_MODE_PARAM } from '../../constants/Consts';
import { getDisplayTitle } from '../../utils/TitleUtility';
import { isGradeUnlocked } from '../../utils/GradeUtility';
import { Loading } from '../../components/Loading';
import { Index as TypingIndex } from '../typing/Index';
import { useTypingResult } from '../../hooks/api/useTypingResult';
import { isFreeModeAtom } from '../../atom/Atom';

interface IndexProps {
  themeType: THEMES;
}

export const Index = ({ themeType }: IndexProps) => {
  const [showFlash, setShowFlash] = useState<boolean>(false);
  const gradeNumber: number = Number(useParams<{ grade: string }>().grade);
  const { getTypingResults, typingResults } = useTypingResult();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFreeMode = useAtomValue(isFreeModeAtom) || searchParams.get(PLAY_MODE_PARAM) === PLAY_MODE.FREE;

  const fetchTypingResults = useCallback(async () => {
    await getTypingResults(themeType);
  }, [getTypingResults, themeType]);

  useEffect(() => {
    fetchTypingResults();
  }, [fetchTypingResults]);

  const handleShowFlash = useCallback(() => {
    if (showFlash) {
      return;
    }
    setShowFlash(true);
  }, [showFlash]);

  useEffect(() => {
    if (!showFlash) {
      return;
    }
    const timer = setTimeout(() => {
      setShowFlash(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [showFlash]);

  const currentTypingResult = useMemo(
    () => typingResults?.find((typingResult) => typingResult.gradeNumber === gradeNumber),
    [gradeNumber, typingResults]
  );

  useEffect(() => {
    if (!typingResults) {
      return;
    }
    if (!currentTypingResult) {
      navigate(`/practice/${themeType}/`);
      return;
    }
    if (!isFreeMode && !isGradeUnlocked(typingResults, gradeNumber)) {
      navigate(`/practice/${themeType}/`);
    }
  }, [typingResults, currentTypingResult, gradeNumber, isFreeMode, themeType, navigate]);

  if (!typingResults || !currentTypingResult) {
    return <Loading />;
  }

  const { characterType } = currentTypingResult;

  return (
    <>
      <Helmet>
        <title>{getDisplayTitle(themeType)}タイピング</title>
      </Helmet>
      <img className="absolute w-full h-screen object-cover" src={`/${themeType}/${characterType}/background.webp`} alt={'背景'} />
      <img
        className="absolute w-full h-screen animate-background-effect"
        src={`/${themeType}/${characterType}/background_effect.webp`}
        alt={'背景エフェクト'}
      />
      <TypingIndex
        themeType={themeType}
        character={characterType}
        typingResult={currentTypingResult}
        showFlash={showFlash}
        handleShowFlash={() => handleShowFlash()}
      />
      {showFlash && <div className="absolute top-0 w-screen h-screen bg-white animate-flash" />}
    </>
  );
};
